import { List } from 'antd';
import moment from 'moment';
import QueueAnim from 'rc-queue-anim';
import React, { FC, useCallback } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { ProjectConversationId, ProjectId, TaskId, UserId } from 'api/models';
import {
  ApprovalNotificationGroup,
  ApprovalObjectType,
  DocumentNotificationGroup,
  NotificationGroup,
  NotificationStatus,
  ProjectConversationNotificationGroup,
  ProjectNotificationGroup,
  SpaceTabNotificationGroup,
  StatusUpdateNotificationGroup,
  TaskNotificationGroup,
} from 'api/notifications';
import { TimesheetApprovalType } from 'api/timesheets/configuration/configuration';
import { StickyWrapper } from 'components/atoms/sticky-wrapper';
import { useHeaderHeight } from 'components/molecules/page';
import { NotificationsEmptyState } from 'components/organisms/notifications/list/content/empty-state';
import { getTimesheetUrl } from 'components/organisms/timesheets/common/utils';
import { theme } from 'components/styles/theme';
import { getTaskDetailUrl, TaskDrawerTab } from 'hooks/use-navigate-to-task';
import { useUser } from 'hooks/use-user';
import { useTimesheetConfiguration } from 'queries/timesheets/configuration';
import { logger } from 'services';
import { Fetched, FetchedLastPage, Fetching } from 'stores/notifications';
import { getProjectConversationDetailUrl } from 'utils/urls';
import { Notification } from '../line-item';
import { AccountsHeader } from './accounts-header';
import { ApprovalHeader } from './approval-header';
import { DocumentHeader } from './document-header';
import { Footer } from './footer';
import styles from './group.module.css';
import { Hanger } from './hanger';
import { ShowMore } from './more';
import { ProjectConversationHeader } from './project-conversation-header';
import { ProjectHeader } from './project-header';
import { ResourceRequestHeader } from './resource-request-header';
import { SpaceTabHeader } from './space-tab-header';
import { StatusUpdateHeader } from './status-update-header';
import { TaskHeader } from './task-header';
import { TimeoffHeader } from './time-off-header';
import { UserTimesheetHeader } from './user-timesheet-header';

const Layer5List = styled(List)`
  z-index: var(--zindex-layer-05);
`;

export interface NotificationContentProps {
  status: NotificationStatus;
  content: Fetched | Fetching | FetchedLastPage;
  callbacks?: {
    onTaskNotificationClick?: (task: TaskNotificationGroup) => void;
    onDocumentNotificationClick?: (document: DocumentNotificationGroup) => void;
    onProjectNotificationClick?: (project: ProjectNotificationGroup) => void;
    onProjectConversationNotificationClick?: (
      projectConversation: ProjectConversationNotificationGroup,
    ) => void;
    onStatusUpdateNotificationClick?: (
      statusUpdate: StatusUpdateNotificationGroup,
    ) => void;
    onSpaceTabNotificationClick?: (spaceTab: SpaceTabNotificationGroup) => void;
    onApprovalNotificationClick?: (approval: ApprovalNotificationGroup) => void;
  };
}

export const Content: FC<NotificationContentProps> = ({
  status,
  content,
  callbacks,
}) => {
  const groups = content[2];
  const lastSeenAt = content[3];
  const available = groups.filter((g) => g[0] !== 'deleted');
  const { push } = useHistory();
  const {
    user: { userId: currentUserId },
  } = useUser();
  const { data: configuration } = useTimesheetConfiguration();

  const onClick = useCallback(
    (g: NotificationGroup) =>
      NotificationGroup.when(
        (p) => {
          const _onClick = callbacks?.onTaskNotificationClick;

          if (_onClick) {
            _onClick(p);

            return;
          }

          const {
            task: { project, id },
          } = p;

          window.location.hash = `task${project.id}!${id}`;
        },
        (p) => {
          const _onClick = callbacks?.onDocumentNotificationClick;

          if (_onClick) {
            _onClick(p);

            return;
          }

          const {
            document: { project, space, tabId },
          } = p;

          push(`/projects/${project.id}/spaces/${space.id}/tabs/${tabId}`);
        },
        ({ project: { projectId } }) => push(`/projects/${projectId}/plan`),
        (group) => {
          const _onClick = callbacks?.onProjectConversationNotificationClick;

          if (_onClick) {
            _onClick(group);

            return;
          }

          const {
            conversation: { project, conversationId },
          } = group;

          push(
            getProjectConversationDetailUrl(
              ProjectId(project.id),
              ProjectConversationId(conversationId),
            ),
          );
        },
        (group) => {
          const {
            statusUpdate: { project, id },
          } = group;
          const _onClick = callbacks?.onStatusUpdateNotificationClick;

          if (_onClick) {
            _onClick(group);

            return;
          }

          push(`/projects/${project.id}/status-updates/${id}`);
        },
        (group) => {
          const _onClick = callbacks?.onSpaceTabNotificationClick;

          if (_onClick) {
            _onClick(group);

            return;
          }

          const {
            spaceTab: { project, space, id },
          } = group;

          push(`/projects/${project.id}/spaces/${space.id}/tabs/${id}`);
        },
        (group) => {
          const { approval } = group;

          if (callbacks?.onApprovalNotificationClick) {
            callbacks.onApprovalNotificationClick?.(group);

            return;
          }

          switch (approval.approvalObjectType) {
            case ApprovalObjectType.Task: {
              const {
                task: { project, id },
              } = approval;

              push(
                getTaskDetailUrl(TaskId(project.id, id), {
                  tab: TaskDrawerTab.Approvals,
                }),
              );
              break;
            }
            case ApprovalObjectType.Document: {
              const {
                document: { project, space, tabId },
              } = approval;

              push(
                `/projects/${project.id}/spaces/${space.id}/tabs/${tabId}?tab=approvals`,
              );
              break;
            }
            default:
              logger.warn('unable to open group', { group });
          }
        },
        (group) => {
          const {
            timesheet: { userId },
          } = group;

          if (
            configuration?.approvalSetting === TimesheetApprovalType.Manager
          ) {
            push(
              getTimesheetUrl({
                currentUserId: UserId(currentUserId),
                userId: UserId(userId),
              }),
            );
          }
          if (
            configuration?.approvalSetting ===
            TimesheetApprovalType.ProjectOwner
          ) {
            push(
              getTimesheetUrl({
                currentUserId: UserId(currentUserId),
                userId: UserId(userId),
                // projectId: { projectId },
              }),
            );
          }
        },
        // empty function because on clicking timeoff notification nothing should happen
        () => {},
        // add finanacials redirect - if any
        () => {},
        // add accounts redirect later
        (group) => {
          const { company } = group;

          push(`/accounts/${company.id}`);
        },
        // add resource request redirect - if any
        () => {},
        (group) => logger.warn('unable to open group', { group }),
      )(g),
    [callbacks, push, configuration?.approvalSetting, currentUserId],
  );
  const headerHeight = useHeaderHeight();

  return available.length === 0 ? (
    <NotificationsEmptyState status={status} />
  ) : (
    <>
      <QueueAnim
        style={{ zIndex: 0 }}
        data-test-id="notifications"
        type={['bottom', status === NotificationStatus.New ? 'right' : 'left']}
        onEnd={({ type, target }) => {
          if (type === 'enter') {
            target.setAttribute('style', '');
          }
        }}
      >
        {available
          .map(([type, group], index) => (
            <div
              className={styles.group}
              key={group.key?.uri ?? index}
              data-test-id="notifications.wrapper"
            >
              <StickyWrapper
                top={
                  headerHeight +
                  theme.filters.height +
                  theme.menu.height +
                  theme.pixels(theme.layout.layout_01)
                }
              >
                <div role="presentation" onClick={() => onClick(group)}>
                  {NotificationGroup.when(
                    (g) => (
                      <TaskHeader task={g.task}>
                        <Hanger group={g as any} />
                      </TaskHeader>
                    ),
                    (g) => (
                      <DocumentHeader document={g.document}>
                        <Hanger group={g as any} />
                      </DocumentHeader>
                    ),
                    (g) => (
                      <ProjectHeader project={g.project}>
                        <Hanger group={g as any} />
                      </ProjectHeader>
                    ),
                    (g) => (
                      <ProjectConversationHeader
                        projectConversation={g.conversation}
                      >
                        <Hanger group={g as any} />
                      </ProjectConversationHeader>
                    ),
                    (g) => (
                      <StatusUpdateHeader statusUpdate={g.statusUpdate}>
                        <Hanger group={g as any} />
                      </StatusUpdateHeader>
                    ),
                    (g) => (
                      <SpaceTabHeader spaceTab={g.spaceTab}>
                        <Hanger group={g as any} />
                      </SpaceTabHeader>
                    ),
                    (g) => (
                      <ApprovalHeader approval={g.approval}>
                        <Hanger group={g as any} />
                      </ApprovalHeader>
                    ),
                    (g) => (
                      <UserTimesheetHeader timesheet={g.timesheet}>
                        <Hanger group={g as any} />
                      </UserTimesheetHeader>
                    ),
                    (g) => (
                      <TimeoffHeader>
                        <Hanger group={g as any} />
                      </TimeoffHeader>
                    ),
                    (g) => (
                      <div>
                        <Hanger group={g as any} />
                      </div>
                    ),
                    (g) => (
                      <AccountsHeader company={g.company}>
                        <Hanger group={g as any} />
                      </AccountsHeader>
                    ),
                    (g) => (
                      <ResourceRequestHeader>
                        <Hanger group={g as any} />
                      </ResourceRequestHeader>
                    ),
                    () => null,
                  )(group)}
                </div>
              </StickyWrapper>
              <Layer5List>
                <QueueAnim appear={false}>
                  {group.notifications?.map((notification) => (
                    <List.Item
                      key={notification.id}
                      className={`${styles.notification} ${
                        moment
                          .utc(notification.timestamp)
                          .isAfter(lastSeenAt, 'second')
                          ? styles.unseen
                          : ''
                      }`}
                    >
                      <Notification
                        group={group}
                        notification={notification}
                        open={() => onClick(group)}
                        callbacks={{
                          onStatusUpdateClick:
                            callbacks?.onStatusUpdateNotificationClick
                              ? (projectId, statusUpdateId) => {
                                  callbacks?.onStatusUpdateNotificationClick?.({
                                    id: statusUpdateId,
                                    key: { statusUpdateId, id: statusUpdateId },
                                    statusUpdate: {
                                      statusUpdateId,
                                      id: statusUpdateId,
                                      projectId,
                                      project: {
                                        projectId,
                                      },
                                    },
                                  } as any);
                                }
                              : undefined,
                        }}
                      />
                    </List.Item>
                  ))}
                  {group.loadMore != null && (
                    <List.Item>
                      <ShowMore
                        loading={type === 'loading'}
                        loadMore={group.loadMore}
                      />
                    </List.Item>
                  )}
                </QueueAnim>
              </Layer5List>
            </div>
          ))
          .concat(<Footer key="footer" content={content} />)}
      </QueueAnim>
    </>
  );
};
